import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Filter,
  X,
  Download,
  Eye,
  FileText,
  Image,
  File,
  Archive,
  RotateCcw,
  ChevronDown,
  MoreHorizontal,
  LayoutGrid,
  Briefcase,
  Laptop,
  Monitor,
  Keyboard,
  Mouse,
  Headphones,
  Video,
  Tablet,
  Smartphone,
  Server,
  Wifi,
  Battery,
  Sofa,
  Layout,
  Users,
  Archive as ArchiveIcon,
  Printer,
  Search as SearchIcon,
  Projector,
  Copy,
  Wind,
  Car,
  Zap,
  Package,
  ShieldCheck,
  Megaphone,
  FolderOpen,
  HardDrive,
  Building2,
  UserPlus,
  ArrowRightLeft,
  Wrench,
  File as FileIcon,
  Box,
  CheckCircle,
  UserCheck,
} from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@jira-clone/ui'
import {
  useAssets,
  useAssetMutations,
  useUsersForAssignment,
  type AssetOption,
  type AssetDraft,
  type AssetCategory,
  type AssetStatus,
  type AssetType,
  CATEGORY_LABELS,
  CATEGORY_ICONS,
  STATUS_LABELS,
  STATUS_COLORS,
  PHYSICAL_CATEGORY_LABELS,
  DIGITAL_CATEGORY_LABELS,
} from '../../shared/hooks/useCrmVocabulary'
import { getApiErrorMessage } from '../../shared/utils/apiError'
import { formatDistanceToNow } from 'date-fns'

const EMPTY_DRAFT: AssetDraft = {
  name: '',
  category: 'LAPTOP',
  description: '',
  fileUrl: '',
  fileName: '',
  fileType: '',
  fileSize: 0,
  tags: [],
  status: 'AVAILABLE',
  expiryDate: '',
  version: 1,
  assetType: 'PHYSICAL',
  physicalData: {
    assetCode: '',
    serialNumber: '',
    brand: '',
    model: '',
    purchaseDate: '',
    purchasePrice: 0,
    currentValue: 0,
    vendor: '',
    invoiceNumber: '',
    purchaseOrderNumber: '',
    department: '',
    location: '',
    warrantyStartDate: '',
    warrantyEndDate: '',
    warrantyProvider: '',
    notes: '',
    imageUrl: '',
  },
}

const FILE_TYPE_OPTIONS = [
  { value: 'PDF', label: 'PDF' },
  { value: 'IMAGE', label: 'Image' },
  { value: 'PRESENTATION', label: 'Presentation' },
  { value: 'DOCUMENT', label: 'Document' },
  { value: 'SPREADSHEET', label: 'Spreadsheet' },
  { value: 'OTHER', label: 'Other' },
]

function getFileTypeFromMime(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'IMAGE'
  if (mimeType === 'application/pdf') return 'PDF'
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'PRESENTATION'
  if (mimeType.includes('word') || mimeType.includes('document')) return 'DOCUMENT'
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'SPREADSHEET'
  return 'OTHER'
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function canPreview(fileType?: string): boolean {
  return fileType ? ['IMAGE', 'PDF'].includes(fileType) : false
}

// Icon component mapping for category icons
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Laptop: Laptop,
  Monitor: Monitor,
  Keyboard: Keyboard,
  Mouse: Mouse,
  Headphones: Headphones,
  Video: Video,
  Tablet: Tablet,
  Smartphone: Smartphone,
  Server: Server,
  Wifi: Wifi,
  Battery: Battery,
  Sofa: Sofa,
  Layout: Layout,
  Users: Users,
  ArchiveIcon: ArchiveIcon,
  Printer: Printer,
  SearchIcon: SearchIcon,
  Projector: Projector,
  Copy: Copy,
  Wind: Wind,
  Car: Car,
  Zap: Zap,
  Package: Package,
  Image: Image,
  FileText: FileText,
  ShieldCheck: ShieldCheck,
  Megaphone: Megaphone,
  FolderOpen: FolderOpen,
}

// Helper to render category icon
function CategoryIcon({ iconName, className = 'h-5 w-5' }: { iconName: string; className?: string }) {
  const IconComponent = ICON_MAP[iconName] || Package
  return <IconComponent className={className} />
}

// Physical (Company) Asset Categories
const PHYSICAL_CATEGORIES: { value: AssetCategory; label: string; icon: string }[] = [
  // IT & Computing
  { value: 'LAPTOP', label: 'Laptop', icon: 'Laptop' },
  { value: 'DESKTOP_PC', label: 'Desktop PC', icon: 'Monitor' },
  { value: 'MONITOR', label: 'Monitor', icon: 'Monitor' },
  { value: 'KEYBOARD', label: 'Keyboard', icon: 'Keyboard' },
  { value: 'MOUSE', label: 'Mouse', icon: 'Mouse' },
  { value: 'HEADSET', label: 'Headset', icon: 'Headphones' },
  { value: 'WEBCAM', label: 'Webcam', icon: 'Video' },
  { value: 'TABLET', label: 'Tablet', icon: 'Tablet' },
  { value: 'MOBILE_PHONE', label: 'Mobile Phone', icon: 'Smartphone' },
  // Server & Network
  { value: 'SERVER', label: 'Server', icon: 'Server' },
  { value: 'NETWORKING_EQUIPMENT', label: 'Networking Equipment', icon: 'Wifi' },
  { value: 'UPS', label: 'UPS', icon: 'Battery' },
  // Office Furniture
  { value: 'OFFICE_CHAIR', label: 'Office Chair', icon: 'Sofa' },
  { value: 'OFFICE_DESK', label: 'Office Desk', icon: 'Layout' },
  { value: 'CONFERENCE_TABLE', label: 'Conference Table', icon: 'Users' },
  { value: 'CABINET', label: 'Cabinet', icon: 'ArchiveIcon' },
  // Office Equipment
  { value: 'PRINTER', label: 'Printer', icon: 'Printer' },
  { value: 'SCANNER', label: 'Scanner', icon: 'SearchIcon' },
  { value: 'PROJECTOR', label: 'Projector', icon: 'Projector' },
  { value: 'PHOTOCOPIER', label: 'Photocopier', icon: 'Copy' },
  // Facilities
  { value: 'AIR_CONDITIONER', label: 'Air Conditioner', icon: 'Wind' },
  // Vehicles
  { value: 'VEHICLE', label: 'Vehicle', icon: 'Car' },
  // Electrical
  { value: 'ELECTRICAL_EQUIPMENT', label: 'Electrical Equipment', icon: 'Zap' },
  // Other
  { value: 'OTHER', label: 'Other', icon: 'Package' },
]

// Digital Asset Categories
const DIGITAL_CATEGORIES: { value: AssetCategory; label: string; icon: string }[] = [
  { value: 'BRAND_ASSETS', label: 'Brand Assets', icon: 'Image' },
  { value: 'SALES_DOCUMENTS', label: 'Sales & Documents', icon: 'FileText' },
  { value: 'LEGAL_COMPLIANCE', label: 'Legal & Compliance', icon: 'ShieldCheck' },
  { value: 'MARKETING', label: 'Marketing', icon: 'Megaphone' },
  { value: 'COMPANY_DOCUMENTS', label: 'Company Documents', icon: 'FolderOpen' },
]

// All categories for 'all' filter
const ALL_CATEGORIES = [...PHYSICAL_CATEGORIES, ...DIGITAL_CATEGORIES]

export default function AssetsAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [categoryFilter, setCategoryFilter] = useState<AssetCategory | 'all'>(searchParams.get('category') as AssetCategory || 'all')
  const [statusFilter, setStatusFilter] = useState<AssetStatus | 'all'>(searchParams.get('status') as AssetStatus || 'all')
  const [fileTypeFilter, setFileTypeFilter] = useState(searchParams.get('fileType') || 'all')
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'))
  const [assetTypeFilter, setAssetTypeFilter] = useState<AssetType | 'all'>(searchParams.get('assetType') as AssetType || 'all')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'replace'>('create')
  const [draft, setDraft] = useState<AssetDraft>(EMPTY_DRAFT)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewAsset, setPreviewAsset] = useState<AssetOption | null>(null)
  const [dropdownOpenId, setDropdownOpenId] = useState<string | null>(null)
  // Assignment dialogs
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [returnDialogOpen, setReturnDialogOpen] = useState(false)
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [assignTargetAsset, setAssignTargetAsset] = useState<AssetOption | null>(null)
  const [assignForm, setAssignForm] = useState({
    assignedTo: '',
    department: '',
    location: '',
    assignmentDate: new Date().toISOString().split('T')[0],
    notes: '',
  })
  const [returnForm, setReturnForm] = useState({
    notes: '',
  })
  const [transferForm, setTransferForm] = useState({
    assignedTo: '',
    department: '',
    location: '',
    transferDate: new Date().toISOString().split('T')[0],
    notes: '',
  })

  // Fetch assets based on selected asset type
  const assetsQuery = useAssets({
    assetType: assetTypeFilter === 'all' ? undefined : assetTypeFilter,
    category: categoryFilter === 'all' ? undefined : categoryFilter,
    status: statusFilter === 'all' ? undefined : statusFilter,
    fileType: fileTypeFilter === 'all' ? undefined : fileTypeFilter,
    search: search || undefined,
    page,
    limit: 50,
  })

  // Determine initial tab from URL params
  const initialTab = assetTypeFilter === 'PHYSICAL' ? 'physical' : assetTypeFilter === 'DIGITAL' ? 'digital' : 'physical'
  const [activeTab, setActiveTab] = useState(initialTab)

  // Sync activeTab with assetTypeFilter when URL changes
  useEffect(() => {
    const tab = assetTypeFilter === 'PHYSICAL' ? 'physical' : assetTypeFilter === 'DIGITAL' ? 'digital' : 'physical'
    setActiveTab(tab)
  }, [assetTypeFilter])

  const mutations = useAssetMutations()
  const usersQuery = useUsersForAssignment()
  const users = usersQuery.data || []
  const rows = assetsQuery.data?.data || []
  const pagination = assetsQuery.data?.pagination

  // Calculate summary counts for Physical Assets
  const physicalRows = rows.filter(r => r.assetType === 'PHYSICAL')
  const summaryCounts = {
    total: physicalRows.length,
    available: physicalRows.filter(r => r.physicalData?.status === 'AVAILABLE').length,
    assigned: physicalRows.filter(r => r.physicalData?.status === 'ASSIGNED').length,
    underMaintenance: physicalRows.filter(r => r.physicalData?.status === 'UNDER_MAINTENANCE').length,
    retired: physicalRows.filter(r => r.physicalData?.status === 'RETIRED' || r.physicalData?.status === 'DISPOSED').length,
  }

  // Sync URL with filters
  useEffect(() => {
    const params: Record<string, string> = {}
    if (search) params.search = search
    if (assetTypeFilter !== 'all') params.assetType = assetTypeFilter
    if (categoryFilter !== 'all') params.category = categoryFilter
    if (statusFilter !== 'all') params.status = statusFilter
    if (fileTypeFilter !== 'all') params.fileType = fileTypeFilter
    if (page > 1) params.page = String(page)
    setSearchParams(params, { replace: true })
  }, [search, assetTypeFilter, categoryFilter, statusFilter, fileTypeFilter, page, setSearchParams])

  // Reset filters when asset type changes
  useEffect(() => {
    setCategoryFilter('all')
    setStatusFilter('all')
    setFileTypeFilter('all')
    setPage(1)
    setSearch('')
  }, [assetTypeFilter])

  const getCategoriesForType = () => {
    if (assetTypeFilter === 'PHYSICAL') return PHYSICAL_CATEGORIES
    if (assetTypeFilter === 'DIGITAL') return DIGITAL_CATEGORIES
    return ALL_CATEGORIES
  }

  const getStatusesForType = () => {
    if (assetTypeFilter === 'PHYSICAL') {
      return ['AVAILABLE', 'ASSIGNED', 'UNDER_MAINTENANCE', 'DAMAGED', 'LOST', 'RETIRED', 'DISPOSED'] as AssetStatus[]
    }
    if (assetTypeFilter === 'DIGITAL') {
      return ['ACTIVE', 'ARCHIVED', 'EXPIRED'] as AssetStatus[]
    }
    return ['AVAILABLE', 'ASSIGNED', 'UNDER_MAINTENANCE', 'DAMAGED', 'LOST', 'RETIRED', 'DISPOSED', 'ACTIVE', 'ARCHIVED', 'EXPIRED'] as AssetStatus[]
  }

  const getDefaultCategory = () => {
    if (assetTypeFilter === 'PHYSICAL') return 'LAPTOP'
    return 'BRAND_ASSETS'
  }

  const getDefaultStatus = () => {
    if (assetTypeFilter === 'PHYSICAL') return 'AVAILABLE'
    return 'ACTIVE'
  }

  const modalCategories = () => {
    if (draft.assetType === 'PHYSICAL') return PHYSICAL_CATEGORIES
    return DIGITAL_CATEGORIES
  }

  const modalStatuses = () => {
    if (draft.assetType === 'PHYSICAL') {
      return ['AVAILABLE', 'ASSIGNED', 'UNDER_MAINTENANCE', 'DAMAGED', 'LOST', 'RETIRED', 'DISPOSED'] as AssetStatus[]
    }
    return ['ACTIVE', 'ARCHIVED', 'EXPIRED'] as AssetStatus[]
  }

  const openCreate = () => {
    const initialPhysicalData = assetTypeFilter === 'PHYSICAL' ? {
      assetCode: '',
      serialNumber: '',
      brand: '',
      model: '',
      purchaseDate: '',
      purchasePrice: 0,
      currentValue: 0,
      vendor: '',
      invoiceNumber: '',
      purchaseOrderNumber: '',
      department: '',
      location: '',
      warrantyStartDate: '',
      warrantyEndDate: '',
      warrantyProvider: '',
      notes: '',
      imageUrl: '',
    } : undefined
    setDraft({ 
      ...EMPTY_DRAFT, 
      assetType: assetTypeFilter === 'all' ? 'DIGITAL' : assetTypeFilter,
      category: getDefaultCategory(),
      status: getDefaultStatus(),
      physicalData: initialPhysicalData,
    })
    setDialogMode('create')
    setEditingId(null)
    setDialogOpen(true)
  }

  const openEdit = (row: AssetOption) => {
    const pd: Record<string, any> = row.physicalData || {}
    const toDateInput = (v: any) => (v ? new Date(v).toISOString().split('T')[0] : '')
    setDraft({
      id: row.id,
      name: row.name,
      category: row.category,
      description: row.description || '',
      fileUrl: row.fileUrl || '',
      fileName: row.fileName || '',
      fileType: row.fileType || '',
      fileSize: row.fileSize || 0,
      tags: row.tags || [],
      status: row.status,
      expiryDate: row.expiryDate ? new Date(row.expiryDate).toISOString().split('T')[0] : '',
      version: row.version || 1,
      assetType: row.assetType,
      physicalData: {
        ...pd,
        purchaseDate: toDateInput(pd.purchaseDate),
        warrantyStartDate: toDateInput(pd.warrantyStartDate),
        warrantyEndDate: toDateInput(pd.warrantyEndDate),
      },
    })
    setDialogMode('edit')
    setEditingId(row.id)
    setDialogOpen(true)
  }

  const openReplace = (row: AssetOption) => {
    const pd: Record<string, any> = row.physicalData || {}
    const toDateInput = (v: any) => (v ? new Date(v).toISOString().split('T')[0] : '')
    setDraft({
      id: row.id,
      name: row.name,
      category: row.category,
      description: row.description || '',
      fileUrl: '',
      fileName: '',
      fileType: row.fileType || '',
      fileSize: 0,
      tags: row.tags || [],
      status: row.status,
      expiryDate: row.expiryDate ? new Date(row.expiryDate).toISOString().split('T')[0] : '',
      version: (row.version || 1) + 1,
      assetType: row.assetType,
      physicalData: {
        ...pd,
        purchaseDate: toDateInput(pd.purchaseDate),
        warrantyStartDate: toDateInput(pd.warrantyStartDate),
        warrantyEndDate: toDateInput(pd.warrantyEndDate),
      },
    })
    setDialogMode('replace')
    setEditingId(row.id)
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.name.trim()) {
      toast.error('Asset name is required.')
      return
    }
    if (!draft.category) {
      toast.error('Category is required.')
      return
    }
    if (draft.assetType === 'DIGITAL' && dialogMode !== 'edit' && (!draft.fileUrl || !draft.fileName || !draft.fileType || !draft.fileSize)) {
      toast.error('File information is required for digital assets.')
      return
    }
    if (draft.assetType === 'PHYSICAL' && dialogMode !== 'edit' && !draft.physicalData?.assetCode) {
      toast.error('Asset code is required for physical assets.')
      return
    }

    const payload: Record<string, any> = {
      name: draft.name.trim(),
      category: draft.category,
      description: draft.description?.trim() || '',
      status: draft.status || getDefaultStatus(),
      assetType: draft.assetType || 'DIGITAL',
    }

    if (draft.assetType === 'DIGITAL') {
      if (dialogMode !== 'edit') {
        payload.digitalData = {
          fileUrl: draft.fileUrl,
          fileName: draft.fileName,
          fileType: draft.fileType,
          fileSize: draft.fileSize,
          tags: draft.tags || [],
          version: draft.version || 1,
        }
      } else {
        payload.digitalData = {
          tags: draft.tags || [],
        }
      }
      if (draft.expiryDate) {
        payload.digitalData = { ...payload.digitalData, expiryDate: new Date(draft.expiryDate).toISOString() }
      } else if (dialogMode === 'edit' && draft.expiryDate === '') {
        payload.digitalData = { ...payload.digitalData, expiryDate: null }
      }
    } else {
      // Physical asset
      const pd = draft.physicalData || {}
      const toNull = (v: any) => (v === '' || v === undefined || v === null) ? null : v
      payload.physicalData = {
        assetCode: pd.assetCode || '',
        serialNumber: toNull(pd.serialNumber),
        brand: toNull(pd.brand),
        model: toNull(pd.model),
        purchaseDate: toNull(pd.purchaseDate),
        purchasePrice: pd.purchasePrice || 0,
        currentValue: pd.currentValue || 0,
        vendor: toNull(pd.vendor),
        invoiceNumber: toNull(pd.invoiceNumber),
        purchaseOrderNumber: toNull(pd.purchaseOrderNumber),
        department: toNull(pd.department),
        location: toNull(pd.location),
        warrantyStartDate: toNull(pd.warrantyStartDate),
        warrantyEndDate: toNull(pd.warrantyEndDate),
        warrantyProvider: toNull(pd.warrantyProvider),
        notes: toNull(pd.notes),
        imageUrl: toNull(pd.imageUrl),
      }
    }

    try {
      if (dialogMode === 'replace' && editingId) {
        await mutations.replace.mutateAsync({
          id: editingId,
          payload: {
            fileUrl: draft.fileUrl,
            fileName: draft.fileName,
            fileType: draft.fileType,
            fileSize: draft.fileSize,
            version: draft.version,
          },
        })
        toast.success('File replaced successfully.')
      } else if (editingId) {
        await mutations.update.mutateAsync({ id: editingId, payload })
        toast.success('Asset updated successfully.')
      } else {
        await mutations.create.mutateAsync(payload)
        toast.success('Asset created successfully.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, dialogMode === 'create' ? 'create this asset' : dialogMode === 'replace' ? 'replace this file' : 'update this asset'))
    }
  }

  const handleDelete = async (row: AssetOption) => {
    if (!confirm(`Delete "${row.name}"? This can't be undone.`)) return
    setDeletingId(row.id)
    try {
      await mutations.remove.mutateAsync(row.id)
      toast.success('Asset deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this asset'))
    } finally {
      setDeletingId(null)
    }
  }

  // Assignment handlers
  const openAssign = (row: AssetOption) => {
    setAssignTargetAsset(row)
    setAssignForm({
      assignedTo: '',
      department: row.physicalData?.department || '',
      location: row.physicalData?.location || '',
      assignmentDate: new Date().toISOString().split('T')[0],
      notes: '',
    })
    setAssignDialogOpen(true)
  }

  const openReturn = (row: AssetOption) => {
    setAssignTargetAsset(row)
    setReturnForm({ notes: '' })
    setReturnDialogOpen(true)
  }

  const openTransfer = (row: AssetOption) => {
    setAssignTargetAsset(row)
    const user = users.find((u: { id: string }) => u.id === row.physicalData?.assignedTo?.toString())
    setTransferForm({
      assignedTo: '',
      department: row.physicalData?.department || '',
      location: row.physicalData?.location || '',
      transferDate: new Date().toISOString().split('T')[0],
      notes: '',
    })
    setTransferDialogOpen(true)
  }

  const handleAssign = async () => {
    if (!assignTargetAsset || !assignForm.assignedTo) {
      toast.error('Please select an employee to assign the asset to.')
      return
    }
    const selectedUser = users.find((u: { id: string; department?: string }) => u.id === assignForm.assignedTo)
    try {
      await mutations.assign.mutateAsync({
        id: assignTargetAsset.id,
        payload: {
          assignedTo: assignForm.assignedTo,
          department: assignForm.department || selectedUser?.department || '',
          location: assignForm.location,
          assignmentDate: assignForm.assignmentDate,
          notes: assignForm.notes,
        },
      })
      toast.success('Asset assigned successfully.')
      setAssignDialogOpen(false)
      setAssignTargetAsset(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'assign this asset'))
    }
  }

  const handleReturn = async () => {
    if (!assignTargetAsset) return
    try {
      await mutations.returnAsset.mutateAsync({
        id: assignTargetAsset.id,
        payload: { notes: returnForm.notes },
      })
      toast.success('Asset returned successfully.')
      setReturnDialogOpen(false)
      setAssignTargetAsset(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'return this asset'))
    }
  }

  const handleTransfer = async () => {
    if (!assignTargetAsset || !transferForm.assignedTo) {
      toast.error('Please select an employee to transfer the asset to.')
      return
    }
    const selectedUser = users.find((u: { id: string; department?: string }) => u.id === transferForm.assignedTo)
    try {
      await mutations.transfer.mutateAsync({
        id: assignTargetAsset.id,
        payload: {
          assignedTo: transferForm.assignedTo,
          department: transferForm.department || selectedUser?.department || '',
          location: transferForm.location,
          transferDate: transferForm.transferDate,
          notes: transferForm.notes,
        },
      })
      toast.success('Asset transferred successfully.')
      setTransferDialogOpen(false)
      setAssignTargetAsset(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'transfer this asset'))
    }
  }

  const handleDownload = (asset: AssetOption) => {
    if (!asset.fileUrl || !asset.fileName) return
    const link = document.createElement('a')
    link.href = asset.fileUrl
    link.download = asset.fileName
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handlePreview = (asset: AssetOption) => {
    if (!asset.fileUrl) return
    setPreviewAsset(asset)
    setPreviewOpen(true)
  }

  const renderAssetRow = (row: AssetOption) => {
    const isPhysical = row.assetType === 'PHYSICAL'
    const isDigital = row.assetType === 'DIGITAL'
    
    if (isPhysical) {
      const pd = row.physicalData
      const catIcon = CATEGORY_ICONS[row.category] || 'Package'
      return (
        <Card key={row.id} className="transition-colors" interactive>
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg shrink-0 border ${STATUS_COLORS[row.status]}`}>
                  <CategoryIcon iconName={catIcon} className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-foreground truncate">{row.name}</p>
                    {pd?.assetCode && (
                      <Badge variant="secondary" className="text-[10px] h-4 px-1.5 font-mono">
                        {pd.assetCode}
                      </Badge>
                    )}
                  </div>
                  {row.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{row.description}</p>
                  )}
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                      {CATEGORY_LABELS[row.category]}
                    </Badge>
                    {pd?.serialNumber && (
                      <>
                        <span className="text-muted-foreground">•</span>
                        <span className="font-mono font-medium text-foreground">SN: {pd.serialNumber}</span>
                      </>
                    )}
                    {pd?.brand && pd?.model && (
                      <>
                        <span className="text-muted-foreground">•</span>
                        <span className="font-mono font-medium text-foreground">{pd.brand} {pd.model}</span>
                      </>
                    )}
                    {pd?.assignedToName && (
                      <>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-foreground">Assigned: {pd.assignedToName}</span>
                      </>
                    )}
                    {pd?.location && (
                      <>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-foreground">{pd.location}</span>
                      </>
                    )}
                    {pd?.purchaseDate && (
                      <>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-foreground">Purchased: {new Date(pd.purchaseDate).toLocaleDateString()}</span>
                      </>
                    )}
                    {pd?.currentValue && (
                      <>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-foreground">₹{pd.currentValue.toLocaleString()}</span>
                      </>
                    )}
                    {pd?.warrantyEndDate && (
                      <>
                        <span className="text-muted-foreground">•</span>
                        <span className={new Date(pd.warrantyEndDate) < new Date() ? 'text-destructive' : 'text-warning'}>
                          Warranty: {row.warrantyStatus || 'Unknown'}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <DropdownMenu open={dropdownOpenId === row.id} onOpenChange={(open) => open ? setDropdownOpenId(row.id) : setDropdownOpenId(null)}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => { window.location.href = `/app/settings/assets/${row.id}`; setDropdownOpenId(null); }} className="flex items-center gap-2">
                      <Eye className="h-3.5 w-3.5" /> View
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { openEdit(row); setDropdownOpenId(null); }} className="flex items-center gap-2">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </DropdownMenuItem>
                    {pd?.status === 'AVAILABLE' && (
                      <DropdownMenuItem onClick={() => { openAssign(row); setDropdownOpenId(null); }} className="flex items-center gap-2">
                        <UserPlus className="h-3.5 w-3.5" /> Assign
                      </DropdownMenuItem>
                    )}
                    {pd?.status === 'ASSIGNED' && (
                      <>
                        <DropdownMenuItem onClick={() => { openReturn(row); setDropdownOpenId(null); }} className="flex items-center gap-2">
                          <RotateCcw className="h-3.5 w-3.5" /> Return
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { openTransfer(row); setDropdownOpenId(null); }} className="flex items-center gap-2">
                          <ArrowRightLeft className="h-3.5 w-3.5" /> Transfer
                        </DropdownMenuItem>
                      </>
                    )}
                    {pd?.status !== 'DISPOSED' && pd?.status !== 'RETIRED' && (
                      <DropdownMenuItem onClick={() => { window.location.href = `/app/settings/assets/${row.id}?tab=maintenance`; setDropdownOpenId(null); }} className="flex items-center gap-2">
                        <Wrench className="h-3.5 w-3.5" /> Maintenance
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => { window.location.href = `/app/settings/assets/${row.id}?tab=documents`; setDropdownOpenId(null); }} className="flex items-center gap-2">
                      <FileIcon className="h-3.5 w-3.5" /> Documents
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { handleDelete(row); setDropdownOpenId(null); }} className="flex items-center gap-2 text-destructive" disabled={deletingId === row.id}>
                      <Trash2 className="h-3.5 w-3.5" /> {deletingId === row.id ? 'Deleting...' : 'Delete'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Badge variant="outline" className={`text-[10px] h-4 px-1.5 ${STATUS_COLORS[row.status]}`}>
                  {STATUS_LABELS[row.status]}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )
    }

    // Digital asset row (existing behavior)
    return (
      <Card key={row.id} className="transition-colors" interactive>
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg shrink-0 border ${STATUS_COLORS[row.status]}`}>
              <CategoryIcon iconName={CATEGORY_ICONS[row.category] || 'Package'} className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground truncate">{row.name}</p>
              {row.description && (
                <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{row.description}</p>
              )}
              <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1 flex-wrap">
                <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                  {CATEGORY_LABELS[row.category]}
                </Badge>
                <span className="text-muted-foreground">•</span>
                <span className="font-mono font-medium text-foreground">{formatFileSize(row.fileSize || 0)}</span>
                <span className="text-muted-foreground">•</span>
                <span className="font-mono font-medium text-foreground">{row.fileType || 'N/A'}</span>
                {row.expiryDate && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <span className={new Date(row.expiryDate) < new Date() ? 'text-destructive' : 'text-warning'}>
                      Expires {formatDistanceToNow(new Date(row.expiryDate), { addSuffix: true })}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <DropdownMenu open={dropdownOpenId === row.id} onOpenChange={(open) => open ? setDropdownOpenId(row.id) : setDropdownOpenId(null)}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => { handlePreview(row); setDropdownOpenId(null); }} className="flex items-center gap-2">
                  <Eye className="h-3.5 w-3.5" /> Preview
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { handleDownload(row); setDropdownOpenId(null); }} className="flex items-center gap-2">
                  <Download className="h-3.5 w-3.5" /> Download
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { openEdit(row); setDropdownOpenId(null); }} className="flex items-center gap-2">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { openReplace(row); setDropdownOpenId(null); }} className="flex items-center gap-2">
                  <RotateCcw className="h-3.5 w-3.5" /> Replace File
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { handleDelete(row); setDropdownOpenId(null); }} className="flex items-center gap-2 text-destructive" disabled={deletingId === row.id}>
                  <Trash2 className="h-3.5 w-3.5" /> {deletingId === row.id ? 'Deleting...' : 'Delete'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Badge variant="outline" className={`text-[10px] h-4 px-1.5 ${STATUS_COLORS[row.status]}`}>
              {STATUS_LABELS[row.status]}
            </Badge>
          </div>
        </CardContent>
      </Card>
    )
  }

  const activeAssetTypeLabel = assetTypeFilter === 'PHYSICAL' ? 'Physical Assets' : assetTypeFilter === 'DIGITAL' ? 'Digital Assets' : 'All Assets'

return (
    <div className="space-y-5 max-w-7xl">
      {/* Header */}
      <div className="flex items-start sm:items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">
            ASSETS
          </span>
        </div>
        <span className="text-xs text-muted-foreground">
          Manage reusable company assets, documents, marketing materials, and business resources from one centralized location.
        </span>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); setAssetTypeFilter(val === 'physical' ? 'PHYSICAL' : 'DIGITAL') }} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="physical" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Building2 className="mr-2 h-4 w-4" /> Company Assets
          </TabsTrigger>
          <TabsTrigger value="digital" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <HardDrive className="mr-2 h-4 w-4" /> Digital Assets
          </TabsTrigger>
        </TabsList>

        <TabsContent value="physical" className="space-y-5 pt-4">
          {/* Summary Cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <Box className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Assets</p>
                    <p className="text-2xl font-bold">{summaryCounts.total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                    <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Available</p>
                    <p className="text-2xl font-bold">{summaryCounts.available}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <UserCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Assigned</p>
                    <p className="text-2xl font-bold">{summaryCounts.assigned}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                    <Wrench className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Under Maintenance</p>
                    <p className="text-2xl font-bold">{summaryCounts.underMaintenance}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800">
                    <Archive className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Retired</p>
                    <p className="text-2xl font-bold">{summaryCounts.retired}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search and Filters for Physical Assets */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search assets..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
                onKeyDown={(e) => e.key === 'Enter' && setPage(1)}
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v as AssetCategory | 'all'); setPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {PHYSICAL_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as AssetStatus | 'all'); setPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="AVAILABLE">Available</SelectItem>
                  <SelectItem value="ASSIGNED">Assigned</SelectItem>
                  <SelectItem value="UNDER_MAINTENANCE">Under Maintenance</SelectItem>
                  <SelectItem value="DAMAGED">Damaged</SelectItem>
                  <SelectItem value="LOST">Lost</SelectItem>
                  <SelectItem value="RETIRED">Retired</SelectItem>
                  <SelectItem value="DISPOSED">Disposed</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={openCreate} className="gap-1.5">
                <Plus className="h-4 w-4" /> Add Physical Asset
              </Button>
            </div>
          </div>

          {/* Active Filters Display */}
          {(search || categoryFilter !== 'all' || statusFilter !== 'all' || fileTypeFilter !== 'all') && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Active filters:</span>
              {search && (
                <Badge variant="secondary" className="gap-1">
                  Search: {search}
                  <button onClick={() => { setSearch(''); setPage(1); }} className="ml-1 hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {categoryFilter !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  {CATEGORY_LABELS[categoryFilter]}
                  <button onClick={() => { setCategoryFilter('all'); setPage(1); }} className="ml-1 hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {statusFilter !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  {STATUS_LABELS[statusFilter as AssetStatus]}
                  <button onClick={() => { setStatusFilter('all'); setPage(1); }} className="ml-1 hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {fileTypeFilter !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  {fileTypeFilter}
                  <button onClick={() => { setFileTypeFilter('all'); setPage(1); }} className="ml-1 hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setCategoryFilter('all'); setStatusFilter('all'); setFileTypeFilter('all'); setPage(1); }}>
                Clear all
              </Button>
            </div>
          )}

          {/* Assets List or Empty State */}
          {assetsQuery.isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-4">
                    <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <svg className="w-14 h-14 text-slate-400 stroke-[1.5]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <p className="mt-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                {search || categoryFilter !== 'all' || statusFilter !== 'all' || fileTypeFilter !== 'all' ? 'No assets match your filters' : 'No assets uploaded yet'}
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {search || categoryFilter !== 'all' || statusFilter !== 'all' || fileTypeFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Click "Upload Asset" to add your first asset'}
              </p>
              {!search && categoryFilter === 'all' && statusFilter === 'all' && fileTypeFilter === 'all' && (
                <Button onClick={openCreate} className="mt-3 gap-1.5">
                  <Plus className="h-4 w-4" /> Upload Asset
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map(renderAssetRow)}
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border pt-4">
              <p className="text-sm text-muted-foreground">
                Showing {((page - 1) * 50) + 1} to {Math.min(page * 50, pagination.total)} of {pagination.total} assets
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronDown className="h-4 w-4 rotate-180" /> Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Digital Assets tab */}
        <TabsContent value="digital" className="pt-4">
          {/* Search and Filters for Digital Assets */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search assets..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
                onKeyDown={(e) => e.key === 'Enter' && setPage(1)}
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v as AssetCategory | 'all'); setPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {DIGITAL_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as AssetStatus | 'all'); setPage(1); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="ARCHIVED">Archived</SelectItem>
                  <SelectItem value="EXPIRED">Expired</SelectItem>
                </SelectContent>
              </Select>
              <Select value={fileTypeFilter} onValueChange={(v) => { setFileTypeFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All File Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All File Types</SelectItem>
                  {FILE_TYPE_OPTIONS.map((ft) => (
                    <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={openCreate} className="gap-1.5">
                <Plus className="h-4 w-4" /> Upload Asset
              </Button>
            </div>
          </div>

          {/* Active Filters Display */}
          {(search || categoryFilter !== 'all' || statusFilter !== 'all' || fileTypeFilter !== 'all') && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Active filters:</span>
              {search && (
                <Badge variant="secondary" className="gap-1">
                  Search: {search}
                  <button onClick={() => { setSearch(''); setPage(1); }} className="ml-1 hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {categoryFilter !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  {CATEGORY_LABELS[categoryFilter]}
                  <button onClick={() => { setCategoryFilter('all'); setPage(1); }} className="ml-1 hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {statusFilter !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  {STATUS_LABELS[statusFilter as AssetStatus]}
                  <button onClick={() => { setStatusFilter('all'); setPage(1); }} className="ml-1 hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {fileTypeFilter !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  {fileTypeFilter}
                  <button onClick={() => { setFileTypeFilter('all'); setPage(1); }} className="ml-1 hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setCategoryFilter('all'); setStatusFilter('all'); setFileTypeFilter('all'); setPage(1); }}>
                Clear all
              </Button>
            </div>
          )}

          {/* Assets List or Empty State */}
          {assetsQuery.isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-4">
                    <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <svg className="w-14 h-14 text-slate-400 stroke-[1.5]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <p className="mt-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                {search || categoryFilter !== 'all' || statusFilter !== 'all' || fileTypeFilter !== 'all' ? 'No assets match your filters' : 'No assets uploaded yet'}
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {search || categoryFilter !== 'all' || statusFilter !== 'all' || fileTypeFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Click "Upload Asset" to add your first asset'}
              </p>
              {!search && categoryFilter === 'all' && statusFilter === 'all' && fileTypeFilter === 'all' && (
                <Button onClick={openCreate} className="mt-3 gap-1.5">
                  <Plus className="h-4 w-4" /> Upload Asset
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map(renderAssetRow)}
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border pt-4">
              <p className="text-sm text-muted-foreground">
                Showing {((page - 1) * 50) + 1} to {Math.min(page * 50, pagination.total)} of {pagination.total} assets
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronDown className="h-4 w-4 rotate-180" /> Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit/Replace Asset Modal */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
          <DialogTitle>
            {dialogMode === 'create' ? (draft.assetType === 'PHYSICAL' ? 'Add Physical Asset' : 'Upload Digital Asset') : dialogMode === 'edit' ? 'Edit Asset' : 'Replace File'}
          </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="asset-name">Asset Name *</Label>
              <Input
                id="asset-name"
                value={draft.name}
                onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Company Brochure 2026"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asset-category">Category *</Label>
              <Select
                value={draft.category}
                onValueChange={(value) => setDraft((d: AssetDraft) => ({ ...d, category: value as AssetCategory }))}
              >
                <SelectTrigger id="asset-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {modalCategories().map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asset-description">Description</Label>
              <Textarea
                id="asset-description"
                value={draft.description}
                onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, description: e.target.value }))}
                placeholder="Brief description of the asset"
                rows={3}
                className="w-full h-24 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              />
            </div>

            {dialogMode !== 'edit' && draft.assetType === 'DIGITAL' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="asset-file">File *</Label>
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                    <Input
                      id="asset-file"
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) {
                          const fileUrl = URL.createObjectURL(file)
                          setDraft((d: AssetDraft) => ({
                            ...d,
                            fileUrl,
                            fileName: file.name,
                            fileType: getFileTypeFromMime(file.type),
                            fileSize: file.size,
                          }))
                        }
                      }}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.svg"
                    />
                    <label htmlFor="asset-file" className="cursor-pointer">
                      <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm font-medium text-foreground">Click to upload or drag and drop</p>
                      <p className="text-xs text-muted-foreground mt-1">PDF, DOC, XLS, PPT, Images (max 10MB)</p>
                    </label>
                    {draft.fileName && (
                      <div className="mt-3 p-3 bg-muted rounded-lg text-left">
                        <p className="text-sm font-medium truncate">{draft.fileName}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-2">
                          <span>{formatFileSize(draft.fileSize || 0)}</span>
                          <span>•</span>
                          <span>{draft.fileType || 'N/A'}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {dialogMode === 'replace' && (
              <div className="space-y-1.5">
                <Label>Current File</Label>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium truncate">{draft.fileName || 'No file selected'}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-2">
                    <span>{formatFileSize(draft.fileSize || 0)}</span>
                    <span>•</span>
                    <span>{draft.fileType || 'N/A'}</span>
                  </p>
                </div>
              </div>
            )}

            {draft.assetType === 'PHYSICAL' && (
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg border">
                <p className="text-sm font-semibold text-foreground">Physical Asset Details</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="asset-code">Asset Code *</Label>
                    <Input
                      id="asset-code"
                      value={draft.physicalData?.assetCode || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), assetCode: e.target.value } }))}
                      placeholder="e.g. LAP-001"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="serial-number">Serial Number</Label>
                    <Input
                      id="serial-number"
                      value={draft.physicalData?.serialNumber || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), serialNumber: e.target.value } }))}
                      placeholder="e.g. SN123456"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="brand">Brand</Label>
                    <Input
                      id="brand"
                      value={draft.physicalData?.brand || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), brand: e.target.value } }))}
                      placeholder="e.g. Dell, HP"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="model">Model</Label>
                    <Input
                      id="model"
                      value={draft.physicalData?.model || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), model: e.target.value } }))}
                      placeholder="e.g. Latitude 5520"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="purchase-date">Purchase Date</Label>
                    <Input
                      id="purchase-date"
                      type="date"
                      value={draft.physicalData?.purchaseDate || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), purchaseDate: e.target.value } }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="purchase-price">Purchase Price</Label>
                    <Input
                      id="purchase-price"
                      type="number"
                      value={draft.physicalData?.purchasePrice || 0}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), purchasePrice: parseFloat(e.target.value) || 0 } }))}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="current-value">Current Value</Label>
                    <Input
                      id="current-value"
                      type="number"
                      value={draft.physicalData?.currentValue || 0}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), currentValue: parseFloat(e.target.value) || 0 } }))}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="vendor">Vendor</Label>
                    <Input
                      id="vendor"
                      value={draft.physicalData?.vendor || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), vendor: e.target.value } }))}
                      placeholder="e.g. Dell Technologies"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="invoice-number">Invoice Number</Label>
                    <Input
                      id="invoice-number"
                      value={draft.physicalData?.invoiceNumber || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), invoiceNumber: e.target.value } }))}
                      placeholder="e.g. INV-001"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="po-number">Purchase Order Number</Label>
                    <Input
                      id="po-number"
                      value={draft.physicalData?.purchaseOrderNumber || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), purchaseOrderNumber: e.target.value } }))}
                      placeholder="e.g. PO-001"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={draft.physicalData?.department || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), department: e.target.value } }))}
                      placeholder="e.g. Engineering"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={draft.physicalData?.location || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), location: e.target.value } }))}
                      placeholder="e.g. Floor 3, Desk 12"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="warranty-start">Warranty Start Date</Label>
                    <Input
                      id="warranty-start"
                      type="date"
                      value={draft.physicalData?.warrantyStartDate || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), warrantyStartDate: e.target.value } }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="warranty-end">Warranty End Date</Label>
                    <Input
                      id="warranty-end"
                      type="date"
                      value={draft.physicalData?.warrantyEndDate || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), warrantyEndDate: e.target.value } }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="warranty-provider">Warranty Provider</Label>
                    <Input
                      id="warranty-provider"
                      value={draft.physicalData?.warrantyProvider || ''}
                      onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), warrantyProvider: e.target.value } }))}
                      placeholder="e.g. Dell Support"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="physical-notes">Notes</Label>
                  <Textarea
                    id="physical-notes"
                    value={draft.physicalData?.notes || ''}
                    onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, physicalData: { ...(d.physicalData || {}), notes: e.target.value } }))}
                    placeholder="Additional notes about this asset"
                    rows={2}
                    className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="asset-tags">Tags</Label>
              <Input
                id="asset-tags"
                value={draft.tags?.join(', ') || ''}
                onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))}
                placeholder="tag1, tag2, tag3"
              />
              <p className="text-xs text-muted-foreground">Comma-separated tags for easier searching</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="asset-status">Status</Label>
              <Select
                value={draft.status || getDefaultStatus()}
                onValueChange={(value) => setDraft((d: AssetDraft) => ({ ...d, status: value as AssetStatus }))}
              >
                <SelectTrigger id="asset-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {modalStatuses().map((status) => (
                    <SelectItem key={status} value={status}>{STATUS_LABELS[status]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="asset-expiry">Expiry Date (Optional)</Label>
              <Input
                id="asset-expiry"
                type="date"
                value={draft.expiryDate || ''}
                onChange={(e) => setDraft((d: AssetDraft) => ({ ...d, expiryDate: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Leave empty if the asset doesn't expire</p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                mutations.create.isPending ||
                mutations.update.isPending ||
                mutations.replace.isPending
              }
            >
              {dialogMode === 'create' ? (draft.assetType === 'PHYSICAL' ? 'Add Asset' : 'Upload Asset') : dialogMode === 'edit' ? 'Save Changes' : 'Replace File'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        {previewAsset && (
          <DialogContent className="max-w-4xl max-h-[90vh] p-0">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border ${STATUS_COLORS[previewAsset.status]}`}>
                  {CATEGORY_ICONS[previewAsset.category]}
                </div>
                <div>
                  <p className="font-semibold">{previewAsset.name}</p>
                  <p className="text-xs text-muted-foreground">{CATEGORY_LABELS[previewAsset.category]} • {formatFileSize(previewAsset.fileSize || 0)} • {previewAsset.fileType || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleDownload(previewAsset)} className="gap-1.5" disabled={!previewAsset.fileUrl || !previewAsset.fileName}>
                  <Download className="h-3.5 w-3.5" /> Download
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setPreviewOpen(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="p-4">
              {canPreview(previewAsset.fileType) && previewAsset.fileType === 'IMAGE' && previewAsset.fileUrl && (
                <img src={previewAsset.fileUrl} alt={previewAsset.name} className="max-w-full max-h-[60vh] rounded-lg shadow-lg" />
              )}
              {canPreview(previewAsset.fileType) && previewAsset.fileType === 'PDF' && previewAsset.fileUrl && (
                <iframe
                  src={previewAsset.fileUrl}
                  className="w-full h-[60vh] rounded-lg border border-border"
                  title={previewAsset.name}
                />
              )}
              {!canPreview(previewAsset.fileType) && (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <FileText className="h-16 w-16 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium text-foreground">Preview unavailable</p>
                  <p className="text-sm text-muted-foreground mt-1">{previewAsset.fileType || 'This file type'} cannot be previewed in the browser</p>
                  <Button onClick={() => handleDownload(previewAsset)} className="mt-4 gap-1.5" disabled={!previewAsset.fileUrl || !previewAsset.fileName}>
                    <Download className="h-4 w-4" /> Download to view
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* Assign Asset Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Asset</DialogTitle>
            <DialogDescription>
              Assign "{assignTargetAsset?.name}" to an employee
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="assign-employee">Employee *</Label>
              <Select
                value={assignForm.assignedTo}
                onValueChange={(value) => {
                  const user = users.find((u: { id: string; department?: string }) => u.id === value)
                  setAssignForm(prev => ({
                    ...prev,
                    assignedTo: value,
                    department: user?.department || prev.department,
                  }))
                }}
              >
                <SelectTrigger id="assign-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user: { id: string; name: string; email: string; department?: string }) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.email}){user.department ? ` - ${user.department}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assign-department">Department</Label>
              <Input
                id="assign-department"
                value={assignForm.department}
                onChange={(e) => setAssignForm(prev => ({ ...prev, department: e.target.value }))}
                placeholder="e.g. Engineering, Sales, Marketing"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assign-location">Location</Label>
              <Input
                id="assign-location"
                value={assignForm.location}
                onChange={(e) => setAssignForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g. Building A, Floor 2, Desk 15"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assign-date">Assignment Date *</Label>
              <Input
                id="assign-date"
                type="date"
                value={assignForm.assignmentDate}
                onChange={(e) => setAssignForm(prev => ({ ...prev, assignmentDate: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assign-notes">Notes</Label>
              <Textarea
                id="assign-notes"
                value={assignForm.notes}
                onChange={(e) => setAssignForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about this assignment"
                rows={2}
                className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={mutations.assign.isPending}
            >
              {mutations.assign.isPending ? 'Assigning...' : 'Assign Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Asset Dialog */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Return Asset</DialogTitle>
            <DialogDescription>
              Return "{assignTargetAsset?.name}" from {assignTargetAsset?.physicalData?.assignedToName || 'employee'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="return-notes">Notes</Label>
              <Textarea
                id="return-notes"
                value={returnForm.notes}
                onChange={(e) => setReturnForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about this return"
                rows={3}
                className="w-full h-24 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setReturnDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReturn}
              disabled={mutations.returnAsset.isPending}
            >
              {mutations.returnAsset.isPending ? 'Returning...' : 'Return Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Asset Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer Asset</DialogTitle>
            <DialogDescription>
              Transfer "{assignTargetAsset?.name}" to another employee
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="transfer-employee">New Employee *</Label>
              <Select
                value={transferForm.assignedTo}
                onValueChange={(value) => {
                  const user = users.find((u: { id: string; department?: string }) => u.id === value)
                  setTransferForm(prev => ({
                    ...prev,
                    assignedTo: value,
                    department: user?.department || prev.department,
                  }))
                }}
              >
                <SelectTrigger id="transfer-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user: { id: string; name: string; email: string; department?: string }) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.email}){user.department ? ` - ${user.department}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="transfer-department">Department</Label>
              <Input
                id="transfer-department"
                value={transferForm.department}
                onChange={(e) => setTransferForm(prev => ({ ...prev, department: e.target.value }))}
                placeholder="e.g. Engineering, Sales, Marketing"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="transfer-location">Location</Label>
              <Input
                id="transfer-location"
                value={transferForm.location}
                onChange={(e) => setTransferForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g. Building A, Floor 2, Desk 15"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="transfer-date">Transfer Date *</Label>
              <Input
                id="transfer-date"
                type="date"
                value={transferForm.transferDate}
                onChange={(e) => setTransferForm(prev => ({ ...prev, transferDate: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="transfer-notes">Notes</Label>
              <Textarea
                id="transfer-notes"
                value={transferForm.notes}
                onChange={(e) => setTransferForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about this transfer"
                rows={2}
                className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleTransfer}
              disabled={mutations.transfer.isPending}
            >
              {mutations.transfer.isPending ? 'Transferring...' : 'Transfer Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}